// S14 — Audit Log BE client.
//
// BE endpoints (under `/api/v1/admin/audit-log`):
//   GET    /api/v1/admin/audit-log               — paged list with filters
//   GET    /api/v1/admin/audit-log/{id}          — single entry detail
//   GET    /api/v1/admin/audit-log/export        — streaming CSV (blob)
//
// The BE auto-emits audit log entries from every write controller; the FE is
// read-only here. CSV export uses a separate permission so we hit it with an
// authenticated blob fetch (NOT via window.open).

import { http, unwrap, unwrapList, type ActionResponse, type PaginationInfo } from './http';
import type {
  AuditLog,
  AuditLogEntry,
  AuditLogPage,
  AuditLogQuery,
} from '@features/audit/types';
import { labelAction, labelEntity } from '@features/audit/labels';

/**
 * ISSUE-58 — BE now serializes `metadataJson` / `oldValuesJson` / `newValuesJson`
 * as `JsonElement?` (object / array / primitive / null), not stringified JSON.
 * We type them as `unknown` here and forward them verbatim; legacy non-JSON
 * rows surface as `null`.
 */
interface AuditLogDto {
  id?: string;
  createdAt?: string;
  userId?: string | null;
  userEmail?: string | null;
  action?: string | null;
  entityType?: string | null;
  entityId?: string | null;
  ipAddress?: string | null;
  userAgent?: string | null;
  correlationId?: string | null;
  metadataJson?: unknown;
  oldValuesJson?: unknown;
  newValuesJson?: unknown;
}

function mapDto(d: AuditLogDto): AuditLog {
  return {
    id: d.id ?? '',
    createdAt: d.createdAt ?? '',
    userId: d.userId ?? null,
    userEmail: d.userEmail ?? null,
    action: d.action ?? '',
    entityType: d.entityType ?? null,
    entityId: d.entityId ?? null,
    ipAddress: d.ipAddress ?? null,
    userAgent: d.userAgent ?? null,
    correlationId: d.correlationId ?? null,
    metadataJson: d.metadataJson ?? null,
    oldValuesJson: d.oldValuesJson ?? null,
    newValuesJson: d.newValuesJson ?? null,
  };
}

/**
 * Adapter — convert canonical BE row to the legacy `AuditLogEntry` shape.
 *
 * ISSUE-58 — `metadataJson` is already a parsed value, so no `JSON.parse`
 * hack is needed. We only narrow to a plain object for `meta` (the legacy
 * field shape); non-object payloads are wrapped as `{ value: <raw> }`.
 */
export function toLegacyEntry(row: AuditLog): AuditLogEntry {
  const actionLabel = labelAction(row.action);
  const entityLabel = labelEntity(row.entityType);
  const summary = row.entityId
    ? `${actionLabel} · ${entityLabel} #${row.entityId.slice(0, 8)}`
    : actionLabel;
  let meta: Record<string, unknown> | undefined;
  if (row.metadataJson !== null && row.metadataJson !== undefined) {
    if (
      typeof row.metadataJson === 'object' &&
      !Array.isArray(row.metadataJson)
    ) {
      meta = row.metadataJson as Record<string, unknown>;
    } else {
      meta = { value: row.metadataJson };
    }
  }
  return {
    id: row.id,
    at: row.createdAt,
    actorId: row.userId,
    actorName: row.userEmail ?? 'Sistem',
    actorRole: null,
    action: row.action,
    resource: row.entityType ?? 'unknown',
    resourceId: row.entityId ?? undefined,
    summary,
    meta,
    ip: row.ipAddress ?? undefined,
    userAgent: row.userAgent ?? undefined,
  };
}

function buildQuery(q: AuditLogQuery): URLSearchParams {
  const p = new URLSearchParams();
  const userId = q.userId ?? q.actorId;
  const entityType = q.entityType ?? q.resource;
  const fromUtc = q.fromUtc ?? q.from;
  const toUtc = q.toUtc ?? q.to;
  const userEmail = q.userEmail ?? q.q;
  if (userId) p.set('userId', userId);
  if (userEmail) p.set('userEmail', userEmail);
  if (q.action) p.set('action', q.action);
  if (entityType) p.set('entityType', entityType);
  if (q.entityId) p.set('entityId', q.entityId);
  if (fromUtc) p.set('fromUtc', fromUtc);
  if (toUtc) p.set('toUtc', toUtc);
  if (q.correlationId) p.set('correlationId', q.correlationId);
  return p;
}

export const auditLogApi = {
  async list(query: AuditLogQuery = {}): Promise<AuditLogPage> {
    const p = buildQuery(query);
    if (query.page) p.set('page', String(query.page));
    if (query.pageSize) p.set('pageSize', String(query.pageSize));
    const qs = p.toString();
    const res = await http.get<ActionResponse<AuditLogDto>>(
      `/api/v1/admin/audit-log${qs ? `?${qs}` : ''}`,
    );
    const { items, pagination } = unwrapList(res);
    const raw = items.map(mapDto);
    const legacy = raw.map(toLegacyEntry);
    const page = pagination?.page ?? query.page ?? 1;
    const pageSize = pagination?.pageSize ?? query.pageSize ?? 50;
    const total = pagination?.totalCount ?? legacy.length;
    return {
      items: legacy,
      raw,
      total,
      page,
      pageSize,
      totalPages: pagination?.totalPages ?? Math.max(1, Math.ceil(total / Math.max(1, pageSize))),
    };
  },

  async get(id: string): Promise<AuditLog> {
    const res = await http.get<ActionResponse<AuditLogDto>>(
      `/api/v1/admin/audit-log/${id}`,
    );
    return mapDto(unwrap(res));
  },

  /**
   * Streams a CSV blob from the BE. The endpoint requires the `audit.export`
   * permission (separate from `audit.read`), so admin-only.
   */
  async exportCsv(query: AuditLogQuery = {}): Promise<Blob> {
    const p = buildQuery(query);
    const qs = p.toString();
    const res = await http.get(
      `/api/v1/admin/audit-log/export${qs ? `?${qs}` : ''}`,
      { responseType: 'blob' },
    );
    return res.data as Blob;
  },
};

export type { PaginationInfo };
